import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatCardModule } from "@angular/material/card";
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent, MatTabsModule } from "@angular/material/tabs";
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { GestioneCrawlingService } from '../../services/gestione-crawling.service';
import { NotificationService } from '../../services/notification.service';
import { BreadcrumbsComponent } from "../../shared/breadcrumbs/breadcrumbs.component";
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";
import { CrawlingWebModalComponent } from '../specific-modals/crawling-web-modal/crawling-web-modal.component';

@Component({
  selector: 'app-crawling-web-detail',
  standalone: true,
  imports: [
    CommonModule,
    BreadcrumbsComponent,
    MatTabsModule,
    MatCardModule,
    MatDialogModule,
    SharedTableComponent
  ],
  templateUrl: './crawling-web-detail.component.html',
  styleUrls: ['./crawling-web-detail.component.scss']
})
export class CrawlingWebDetailComponent implements OnInit, OnDestroy {
  id: string | null = null;
  name: string | null = null;

  selectedTabIndex: number = 0;
  activeTab: string = '';
  sitemapOrUrlTabName: string = ''; 

  isLoading: boolean = false; 

  // Paginazione client-side della tabella urls
  urlsPageIndex = 0;
  urlsPageSize = 10;
  urlsTotalItems = 0;
  allUrls: any[] = [];

  // Paginazione client-side della tabella schedules
  triggerPageIndex = 0;
  triggerPageSize = 10;
  triggerTotalItems = 0;
  allTriggers: any[] = [];

  singleJobScheduleElement: any = {};

  // MatTableDataSource tipizzate
  dataSourceUrls = new MatTableDataSource<any>();
  dataSourceSchedules = new MatTableDataSource<any>();

  // config modale per tab attivo
  tableConfigs: Record<string, {
    selectOptions?: Record<string, { value: any, label: string }[]>
  }> = {
    urls: {},
    schedules: {}
  };

  /** Subject per cleanup sottoscrizioni */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly crawlingWebService: GestioneCrawlingService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        this.id = params.get('id');
        const encodedName = params.get('name');
        this.name = encodedName ? decodeURIComponent(encodedName) : null;
      });

    const stored = localStorage.getItem('selectedJobScheduleRow');
    if (stored) {
      try {
        this.singleJobScheduleElement = JSON.parse(stored);
      } catch {
        this.singleJobScheduleElement = {};
      }
      console.log('dato singolo', this.singleJobScheduleElement);
    }

    if(this.singleJobScheduleElement.sitemap){
      this.sitemapOrUrlTabName = 'Sitemap'
    } else {
      this.sitemapOrUrlTabName = 'URLS'
    }

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if(params['tab'] === 'schedulazione'){
        this.selectedTabIndex = 1;
        this.activeTab = 'schedules';
      } else {
        this.selectedTabIndex = 0;
        this.activeTab = 'urls';
      }
    })
    

    if (this.id) {
      this.loadUrlsList();
      this.loadSchedulesList();
    }

    console.log("tabAttiva", this.activeTab);
  }

  // è la lista di url del job 
  loadUrlsList(): void {
    const urls: string[] = this.singleJobScheduleElement?.urls ?? [];
    this.allUrls = urls.map((u: string) => ({ url: u }));
    this.urlsTotalItems = this.allUrls.length ?? 0;
    this.updateUrlPage();

    console.log('total items', this.urlsTotalItems);
  }

  onUrlPageChanged(ev: PageEvent): void {
    this.urlsPageIndex = ev.pageIndex;
    this.urlsPageSize = ev.pageSize;
    this.updateUrlPage();
  }

  private updateUrlPage(): void {
    const start = this.urlsPageIndex * this.urlsPageSize;
    const end = start + this.urlsPageSize;
    this.dataSourceUrls.data = this.allUrls.slice(start, end);
  }

  // è la lista degli schedule del job
  loadSchedulesList(): void {
    const triggersArray = this.singleJobScheduleElement?.triggers || [];
    if(triggersArray && triggersArray.length > 0){
      this.allTriggers = triggersArray.map((t: any) => {
        const parsed = this.parseCron(t.cronExpression);
        return {
          ...parsed,
          id: t.id,
          cronExpression: t.cronExpression
        };
      });
    } else {
      this.allTriggers = [];
    }
    this.triggerTotalItems = this.allTriggers.length ?? 0;
    this.updateTriggerPage();
    console.log('schedules list data', this.dataSourceSchedules.data);
  }

  onTriggerPageChanged(ev: PageEvent): void {
    this.triggerPageIndex = ev.pageIndex;
    this.triggerPageSize = ev.pageSize;
    this.updateTriggerPage();
  }

  private updateTriggerPage(): void {
    const start = this.triggerPageIndex * this.triggerPageSize;
    const end = start + this.triggerPageSize;
    //passo al datasource solo i dati della pagina corrente
    this.dataSourceSchedules.data = this.allTriggers.slice(start, end);
  }

  private parseCron(cronExpr: string): { selectDay: string[], selectHour: string, selectMinute: string } {
    
    if(!cronExpr) return { selectDay: [], selectHour: '*', selectMinute: '*' };
    const parts = cronExpr.trim().split(/\s+/);
    // cron atteso: "m h * * d"
    const minute = parts[0] ?? '*';
    const hour = parts[1] ?? '*';
    const day = parts[4] ?? '*';

    const daysArray = day !== '*' ? day.split(',') : [];
    return { selectDay: daysArray, selectHour: hour, selectMinute: minute };
  }

  private updateCronExpressionInLocalStorage(
    key: string,
    result: { selectDay: string[], selectHour: string, selectMinute: string },
    triggerId?: number
  ) {

    //passo da array a stringa separata da virgola
    const daysStr = result.selectDay && result.selectDay.length > 0 ? result.selectDay.join(',') : '*';
    // creo la stringa in formato cron
    const cronExpr = `${result.selectMinute} ${result.selectHour} * * ${daysStr}`;

    //racchiudo cronExpression all'interno dell'oggetto trigger
    this.singleJobScheduleElement.trigger = {
      cronExpression: cronExpr
    };

    if(triggerId !== undefined && triggerId !== null){
      this.singleJobScheduleElement.trigger.id = triggerId;
    }

    // aggiornamento del local storage
    localStorage.setItem(key, JSON.stringify(this.singleJobScheduleElement));

    return this.singleJobScheduleElement;
  }

  onTabChanged(event: MatTabChangeEvent): void {
    switch (event.index) {
      case 0:
        this.activeTab = 'urls';
        break;
      case 1:
        this.activeTab = 'schedules';
        break;
      default:
        this.activeTab = '';
    }
  }

  private mergeUrlsInLocalStorage(key: string, rawPayload: { extraInputUrlsArray: { url: string }[] }) {
    console.log('result dettaglio', rawPayload);
    // estraggo l'array di urls dal local storage
    const storedUrls: string[] = this.singleJobScheduleElement.urls ?? [];

    // estraggo i nuovi urls dal risultato della modale
    const newUrls = (rawPayload.extraInputUrlsArray ?? []).map((item: any) => item.url).filter((url: string) => url && url.trim().length > 0);

    // unione evitando i duplicati
    this.singleJobScheduleElement.urls = Array.from(new Set([...storedUrls, ...newUrls]));

    // aggiornamento del local storage
    localStorage.setItem(key, JSON.stringify(this.singleJobScheduleElement));

    return this.singleJobScheduleElement;
  }

  private removeUrlFromLocalStorage(key: string, rawPayload: { url: string }) {
    // estraggo l'array di urls dal local storage
    const storedUrls: string[] = this.singleJobScheduleElement.urls ?? [];

    // filtro l'array togliendo l'url passato
    this.singleJobScheduleElement.urls = storedUrls.filter(url => url !== rawPayload.url);

    // aggiornamento del local storage
    localStorage.setItem(key, JSON.stringify(this.singleJobScheduleElement));

    return this.singleJobScheduleElement;
  }
 
  private modifyUrlsInLocalStorage(key: string, oldUrl: string, newUrl: string) {
    
    const storedUrls: string[] = this.singleJobScheduleElement.urls ?? [];
    const index = storedUrls.indexOf(oldUrl);

    //se trovo il vecchio url, lo sovrascrivo col nuovo
    if(index !== -1){
      storedUrls[index] = newUrl;
    }
    this.singleJobScheduleElement.urls = storedUrls;
    //aggiorno il local storage
    localStorage.setItem(key, JSON.stringify(this.singleJobScheduleElement));

    return this.singleJobScheduleElement;
  }

  private readTxtFileAsync(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const text = e.target?.result as string;
        if(text){
          const extractedUrls = text.split(/[,\r\n]+/).map(url => url.trim()).filter(url => url.length > 0);
          resolve(extractedUrls);
        } else {
          resolve([]);
        }
      }
      reader.onerror = (error) => {
        reject(error);
      }
      
      reader.readAsText(file);
    })
  }

  exportUrls(event: any){
    console.log('export!!');
    this.isLoading = true;
    this.crawlingWebService.downloadUrlsTxt(this.id!).pipe(finalize(()=>{this.isLoading = false})).subscribe({
          next: (txt: string) => {
            this.downloadTxt(txt);
            this.notificationService.exportSuccess();
          },
          error: (err) => {
            this.notificationService.exportError();
            console.log("errore nella richiesta", err);
          }
        })
  }

  downloadTxt(txt: string){
    const blob = new Blob([txt], {type: 'text/plain;charset=utf-8;'});
    const url = window.URL.createObjectURL(blob);
    const fileName = `job_schedule_${this.id}_urls_export.txt`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  //metodo centralizzato per evitare le duplicazioni segnalate da sonarqube
  private submitJobScheduleUpdate(
    updatedJobScheduleRow: any, 
    action: 'add' | 'update' | 'delete',
    entityName: 'Url' | 'Sitemap' | 'Schedule', 
    documentFile?: File | null
  ): void {
    const formDataToSubmit = new FormData();
    const payloadString = JSON.stringify(updatedJobScheduleRow);

    const jsonBlob = new Blob([payloadString], {type: 'application/json'});

    formDataToSubmit.append('jobSchedule', jsonBlob);

    if(documentFile) {
      formDataToSubmit.append('document', documentFile);
    }

    this.crawlingWebService.editJobSchedule(formDataToSubmit).pipe(finalize(() => { this.isLoading = false; })).subscribe({
      next: (updatedJobResponse: any) => {
        
        if(updatedJobResponse){
          this.singleJobScheduleElement = updatedJobResponse;
          localStorage.setItem('selectedJobScheduleRow', JSON.stringify(this.singleJobScheduleElement));
        }
        

        if(entityName === 'Url' || entityName === 'Sitemap'){
          this.loadUrlsList();
        } else {
          this.loadSchedulesList();
        }
        this.notificationService.entitySuccess(action, entityName);
      },
      error: (err) => {
        console.log("Errore nella richiesta", err);
        this.notificationService.entityError(action, entityName);
      } 
    })
  }

  openDialog(event: { data: any, type: 'add' | 'edit' | 'delete', nameTable?: string }) {
    const activatedTab = this.activeTab;
    console.log('tab attivo', activatedTab);
    console.log('evento', event);
    const config = this.tableConfigs[activatedTab];

    const payloadWithInfoSitemap = {
      ...event.data,
      sitemap:this.singleJobScheduleElement?.sitemap || false
    }

    const dialogRef = this.dialog.open(CrawlingWebModalComponent, {
      width: '400px',
      data: {
        action: event.type,
        payload: payloadWithInfoSitemap,
        selectOptions: config.selectOptions, // opzioni per i campi select,
        nameTable: event.nameTable
      }
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async result => {
        console.log('result detail', result);
        if (result) {
          this.isLoading = true;

          const rawPayload = result.payload;
          const documentFile = result.document;

          // caso urls / sitemap
          if (activatedTab === 'urls') {
            
            const entityName: 'Url' | 'Sitemap' = this.singleJobScheduleElement?.sitemap ? 'Sitemap' : 'Url';

            if (event.type === 'add') {
              
              if(documentFile){
                try {
                  const fileUrls = await this.readTxtFileAsync(documentFile);
                  if(!rawPayload.extraInputUrlsArray) {
                    rawPayload.extraInputUrlsArray = [];
                  }
                  fileUrls.forEach(url => {
                    rawPayload.extraInputUrlsArray.push({url: url});
                  })
                } catch (error) {
                  console.error('Errore durante la lettura del documento TXT', error);
                  this.isLoading = false;
                  this.notificationService.entityError('add', 'Url - Errore lettura file');
                  return;
                }
              }
              const updatedJobScheduleRow = this.mergeUrlsInLocalStorage('selectedJobScheduleRow', rawPayload);
              console.log('payload detail', updatedJobScheduleRow);
              
              this.submitJobScheduleUpdate(updatedJobScheduleRow, 'add', 'Url', documentFile);

            } else if(event.type === 'edit') {
               console.log('result dettaglio edit', rawPayload);
               const oldUrl = event.data.url;
               const newUrl = rawPayload.extraInputUrlsArray[0]?.url;

               if(oldUrl && newUrl && oldUrl !== newUrl){
                
                //url correnti
                const storedUrls: string[] = this.singleJobScheduleElement.urls ?? [];

                const isDuplicate = storedUrls.includes(newUrl);

                if(isDuplicate){
                  this.isLoading = false;
                  this.dialog.open(ConfirmModalComponent, {
                    width: '400px',
                    data: {
                      message: `${entityName} già presente in tabella.`,
                      title: 'Attenzione'
                    }
                  });
                  return;
                }
                const updatedJobScheduleRow = this.modifyUrlsInLocalStorage('selectedJobScheduleRow', oldUrl, newUrl);
                this.submitJobScheduleUpdate(updatedJobScheduleRow, 'update', entityName);

               } else {
                //se l'utente preme salva senza aver fatto modifiche
                this.isLoading = false;
               }
            } else if (event.type === 'delete') {
              
              if(this.urlsTotalItems === 1) {
                this.isLoading = false;
                this.notificationService.entityError('delete', entityName);
                return;
              };
              const updatedJobScheduleRow = this.removeUrlFromLocalStorage('selectedJobScheduleRow', rawPayload);
              console.log('payload detail', updatedJobScheduleRow);
              this.submitJobScheduleUpdate(updatedJobScheduleRow, 'delete', 'Url');
            }
          }

          // caso schedules
          if (activatedTab === 'schedules'){
            if (event.type === 'edit' || event.type === 'add') {
            console.log('risultato edit or add schedule', rawPayload);

            //recupero l'id dello schedule della riga cliccata (in caso di edit)
            const currentTriggerId = event.type === 'edit' ? event.data?.id : undefined;

            const updatedJobScheduleRow = this.updateCronExpressionInLocalStorage(
              'selectedJobScheduleRow',
              rawPayload,
              currentTriggerId
            );
            console.log('payload', updatedJobScheduleRow);
            const actionType = event.type === 'add' ? 'add' : 'update';
            this.submitJobScheduleUpdate(updatedJobScheduleRow, actionType, 'Schedule');

          } else if (event.type === 'delete') {
              
              const triggerId = event.data?.id;
              if(!triggerId){
                this.isLoading = false;
                console.error('Nessun trigger ID trovato');
                return;
              }
              if(this.triggerTotalItems === 1) {
                this.isLoading = false;
                this.notificationService.entityError('delete', 'Schedule');
                return;
              };

              this.crawlingWebService.deleteJobScheduleTrigger(triggerId).pipe(finalize(() => this.isLoading = false)).subscribe({
                next: () => {
                  this.singleJobScheduleElement.triggers = this.singleJobScheduleElement.triggers.filter((t: any) => t.id !== triggerId);

                  localStorage.setItem('selectedJobScheduleRow', JSON.stringify(this.singleJobScheduleElement));
                  this.loadSchedulesList();
                  this.notificationService.entitySuccess('delete', 'Schedule');
                },
                error: (err) => {
                  console.log('Errore durante la cancellazione del trigger', err);
                  this.notificationService.entityError('delete', 'Schedule');
                }
              });
             
            }
          }
          
        }
      });
  }

  ngOnDestroy(): void {
    // cleanup sottoscrizioni
    this.destroy$.next();
    this.destroy$.complete();

    // pulizia storage
    localStorage.removeItem('selectedJobScheduleRow');
  }
}